import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, Star, MapPin, Wifi, BadgeCheck, FlaskConical, DollarSign, Sparkles, Loader2, Send } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { PageHero, Section, SectionHeading } from "@/components/blocks";
import { toast } from "sonner";
import api from "@/lib/api";

const SUBJECTS = ["Mathematics", "Science", "English", "Humanities", "Business Management", "Accounting"];
const LANGUAGES = ["Mandarin", "Japanese", "Korean", "English Support"];
const YEAR_LEVELS = ["Primary", "Year 7-9", "Year 10-12"];
const MODES = ["Online", "In-person"];
const ANY = "any";

const BENEFITS = ["Earn additional income", "Gain teaching experience", "Build confidence", "Develop professional skills"];
const IDEAL = ["Pre-service teachers", "Graduate teachers", "Experienced educators"];
const CLEARANCES = [
  "WWCC (VIC)", "WWCC (NSW)", "Blue Card (QLD)", "WWCC (WA)", "WWVP (ACT)",
  "Working with Children Check (SA)", "Working with Vulnerable People (TAS/NT)",
  "National Police Check", "International Police Check",
];

function BecomeTutorForm() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", location: "", subjects: "", year_levels: "", mode: "",
    availability: "", rate: "", qualifications: "", experience: "", about: "",
    check_type: "", check_number: "", check_expiry: "", marketing_opt_in: false,
  });
  const [files, setFiles] = useState({ photo: null, clearance: null, transcript: null });
  const [submitting, setSubmitting] = useState(false);
  const setFile = (key) => (e) => setFiles((f) => ({ ...f, [key]: e.target.files?.[0] || null }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.subjects) { toast.error("Please complete the required fields."); return; }
    if (!form.check_type) { toast.error("Please tell us which clearance you hold."); return; }
    if (!files.photo) { toast.error("Please attach a profile photo — families need to see who they're booking."); return; }
    if (!files.clearance) { toast.error("Please attach a copy of your WWCC or police check."); return; }
    setSubmitting(true);
    try {
      const data = new FormData();
      Object.entries(form).forEach(([k, v]) => data.append(k, v));
      Object.entries(files).forEach(([k, v]) => { if (v) data.append(k, v); });
      await api.post("/tutors/apply", data, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Application submitted! We'll review your credentials and be in touch.");
      setForm({ name: "", email: "", phone: "", location: "", subjects: "", year_levels: "", mode: "",
                availability: "", rate: "", qualifications: "", experience: "", about: "",
                check_type: "", check_number: "", check_expiry: "", marketing_opt_in: false });
      setFiles({ photo: null, clearance: null, transcript: null });
      e.target.reset();
    } catch (err) { toast.error(err?.response?.data?.detail || "Something went wrong. Please try again."); }
    finally { setSubmitting(false); }
  };
  const fileInput = "mt-1.5 block w-full text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-teal-50 file:text-teal-700 hover:file:bg-teal-100 cursor-pointer";
  const field = (id, key, label, type = "text") => (
    <div><Label htmlFor={id}>{label}</Label>
      <Input id={id} type={type} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} className="mt-1.5" data-testid={`become-tutor-${key}`} />
    </div>
  );
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="become-tutor-form">
      <div className="grid sm:grid-cols-2 gap-4">
        {field("bt-name", "name", "Full name *")}
        {field("bt-email", "email", "Email *", "email")}
        {field("bt-phone", "phone", "Phone number")}
        {field("bt-location", "location", "Suburb / state")}
      </div>
      <div><Label htmlFor="bt-subjects">Subjects / languages you teach *</Label><Input id="bt-subjects" value={form.subjects} onChange={(e) => setForm({ ...form, subjects: e.target.value })} className="mt-1.5" data-testid="become-tutor-subjects" /></div>
      <div className="grid sm:grid-cols-3 gap-4">
        {field("bt-years", "year_levels", "Year levels")}
        {field("bt-mode", "mode", "Online, in person or both")}
        {field("bt-rate", "rate", "Hourly rate (AUD)")}
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {field("bt-availability", "availability", "Availability (days / times)")}
        {field("bt-quals", "qualifications", "Qualifications (degree, institution)")}
      </div>
      <div><Label htmlFor="bt-exp">Your teaching experience</Label><Textarea id="bt-exp" rows={3} value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} className="mt-1.5" data-testid="become-tutor-experience" /></div>
      <div><Label htmlFor="bt-about">A short bio for your profile <span className="text-slate-400">(families will read this)</span></Label><Textarea id="bt-about" rows={3} value={form.about} onChange={(e) => setForm({ ...form, about: e.target.value })} className="mt-1.5" data-testid="become-tutor-about" /></div>

      <div className="rounded-xl border border-slate-200 bg-slate-50 p-4 space-y-4">
        <div>
          <h4 className="font-display font-semibold text-slate-900 text-sm">Working with children clearance *</h4>
          <p className="text-xs text-slate-500 mt-1">A valid WWCC (or your state equivalent) is required. International applicants may provide an overseas police check.</p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="bt-check-type">Clearance type *</Label>
            <Select value={form.check_type} onValueChange={(v) => setForm({ ...form, check_type: v })}>
              <SelectTrigger id="bt-check-type" className="mt-1.5" data-testid="become-tutor-check-type"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {CLEARANCES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label htmlFor="bt-check-number">Card / reference number</Label><Input id="bt-check-number" value={form.check_number} onChange={(e) => setForm({ ...form, check_number: e.target.value })} className="mt-1.5" data-testid="become-tutor-check-number" /></div>
          <div><Label htmlFor="bt-check-expiry">Expiry date</Label><Input id="bt-check-expiry" type="date" value={form.check_expiry} onChange={(e) => setForm({ ...form, check_expiry: e.target.value })} className="mt-1.5" data-testid="become-tutor-check-expiry" /></div>
        </div>
        <div>
          <Label htmlFor="bt-clearance">Upload your WWCC / police check * <span className="text-slate-400">(PDF or image, max 10 MB)</span></Label>
          <input id="bt-clearance" type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={setFile("clearance")} className={fileInput} data-testid="become-tutor-clearance" />
        </div>
      </div>

      <div>
        <Label htmlFor="bt-photo">Profile photo for your listing * <span className="text-slate-400">(JPG or PNG, max 10 MB)</span></Label>
        <p className="text-xs text-slate-500 mt-1">A clear, friendly headshot — this is what families will see when they browse tutors.</p>
        <input id="bt-photo" type="file" accept=".jpg,.jpeg,.png,.webp" onChange={setFile("photo")} className={fileInput} data-testid="become-tutor-photo" />
      </div>
      <div>
        <Label htmlFor="bt-transcript">Academic transcript or qualifications <span className="text-slate-400">(PDF or Word, max 10 MB)</span></Label>
        <input id="bt-transcript" type="file" accept=".pdf,.docx" onChange={setFile("transcript")} className={fileInput} data-testid="become-tutor-transcript" />
      </div>
      <label className="flex items-start gap-2.5 text-sm text-slate-600 cursor-pointer select-none">
        <input type="checkbox" checked={form.marketing_opt_in} onChange={(e) => setForm({ ...form, marketing_opt_in: e.target.checked })}
               className="mt-0.5 w-4 h-4 rounded border-slate-300 text-teal-600 focus:ring-teal-500 shrink-0" data-testid="become-tutor-optin" />
        <span>Email me occasional teaching tips, tutoring opportunities and new resources.</span>
      </label>

      <p className="text-xs text-slate-500">We'll review your credentials and contact you to confirm you're the right fit before your profile goes live and is marked as verified.</p>
      <Button type="submit" disabled={submitting} className="bg-emerald-600 hover:bg-emerald-700 rounded-full px-6" data-testid="become-tutor-submit">
        {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Apply to become a tutor</>}
      </Button>
    </form>
  );
}

export default function Tutoring() {
  const [tutors, setTutors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ subject: ANY, language: ANY, year_level: ANY, mode: ANY, q: "" });

  const fetchTutors = () => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== ANY) params[k] = v; });
    api.get("/tutors", { params }).then((r) => setTutors(r.data)).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { fetchTutors(); /* eslint-disable-next-line */ }, [filters]);

  return (
    <div data-testid="tutoring-page">
      <PageHero
        eyebrow="Tutoring Marketplace"
        title="Connecting families with exceptional educators."
        subtitle="Find qualified tutors and language educators — or join our marketplace and start earning as you build your teaching career."
        image="https://images.unsplash.com/photo-1573496130103-a442a3754d0e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600"
      />

      <Section>
        <Tabs defaultValue="find">
          <TabsList className="mb-8" data-testid="tutoring-tabs">
            <TabsTrigger value="find" data-testid="tutoring-tab-find"><Search className="w-4 h-4 mr-1.5" /> Find a Tutor</TabsTrigger>
            <TabsTrigger value="become" data-testid="tutoring-tab-become"><Sparkles className="w-4 h-4 mr-1.5" /> Become a Tutor</TabsTrigger>
          </TabsList>

          <TabsContent value="find">
            <Card className="p-5 border-slate-200 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-3">
                <div className="lg:col-span-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input placeholder="Search…" value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="pl-9" data-testid="tutor-search-input" />
                </div>
                <FilterSelect label="Subject" value={filters.subject} onChange={(v) => setFilters({ ...filters, subject: v })} options={SUBJECTS} testId="tutor-filter-subject" />
                <FilterSelect label="Language" value={filters.language} onChange={(v) => setFilters({ ...filters, language: v })} options={LANGUAGES} testId="tutor-filter-language" />
                <FilterSelect label="Year level" value={filters.year_level} onChange={(v) => setFilters({ ...filters, year_level: v })} options={YEAR_LEVELS} testId="tutor-filter-year" />
                <FilterSelect label="Mode" value={filters.mode} onChange={(v) => setFilters({ ...filters, mode: v })} options={MODES} testId="tutor-filter-mode" />
              </div>
            </Card>

            {loading ? (
              <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-teal-600" /></div>
            ) : tutors.length === 0 ? (
              <p className="text-center text-slate-500 py-16">No tutors match your filters yet. Try adjusting your search.</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {tutors.map((t) => (
                  <Card key={t.id} className="overflow-hidden border-slate-200 hover:shadow-xl transition-shadow group" data-testid={`tutor-card-${t.id}`}>
                    <div className="relative h-44">
                      {t.photo
                        ? <img src={t.photo} alt={t.name} className="w-full h-full object-cover" />
                        : <div className="w-full h-full bg-slate-100" />}
                      {t.verified
                        ? <Badge className="absolute top-3 left-3 bg-white text-teal-700 hover:bg-white" data-testid={`tutor-verified-${t.id}`}><BadgeCheck className="w-3.5 h-3.5 mr-1" /> Verified</Badge>
                        : t.sample
                          ? <Badge className="absolute top-3 left-3 bg-slate-900/85 text-white hover:bg-slate-900/85" data-testid={`tutor-sample-${t.id}`}><FlaskConical className="w-3.5 h-3.5 mr-1" /> Sample profile</Badge>
                          : null}
                    </div>
                    <div className="p-5">
                      <div className="flex items-center justify-between">
                        <h3 className="font-display font-bold text-slate-900">{t.name}</h3>
                        {t.rating
                          ? <span className="flex items-center gap-1 text-sm font-semibold text-slate-700" data-testid={`tutor-rating-${t.id}`}><Star className="w-4 h-4 fill-amber-400 text-amber-400" /> {t.rating}<span className="text-slate-400 font-normal">({t.review_count})</span></span>
                          : <span className="text-xs font-semibold text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full whitespace-nowrap" data-testid={`tutor-new-${t.id}`}>New tutor</span>}
                      </div>
                      <p className="text-sm text-slate-600 mt-1 line-clamp-2">{t.intro}</p>
                      <div className="flex flex-wrap gap-1.5 mt-3">
                        {t.subjects.slice(0, 3).map((s) => <Badge key={s} variant="secondary" className="text-[10px]">{s}</Badge>)}
                      </div>
                      <div className="flex items-center gap-4 mt-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {t.location}</span>
                        <span className="flex items-center gap-1"><DollarSign className="w-3 h-3" /> ${t.rate}/hr</span>
                      </div>
                      <Button asChild className="w-full mt-4 bg-slate-900 hover:bg-teal-700 rounded-full" data-testid={`tutor-view-${t.id}`}>
                        <Link to={`/tutoring/${t.id}`}>View profile</Link>
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="become">
            <div className="grid lg:grid-cols-2 gap-10">
              <div>
                <SectionHeading eyebrow="Become a Tutor" title="Turn your teaching skills into income" />
                <p className="text-slate-600 mb-6">Ideal for pre-service teachers, graduate teachers and experienced educators looking to grow.</p>
                <h4 className="font-display font-semibold text-slate-900 mb-3">Ideal for</h4>
                <div className="flex flex-wrap gap-2 mb-6">{IDEAL.map((i) => <Badge key={i} variant="secondary">{i}</Badge>)}</div>
                <h4 className="font-display font-semibold text-slate-900 mb-3">Benefits</h4>
                <ul className="space-y-2.5">
                  {BENEFITS.map((b) => <li key={b} className="flex items-center gap-2 text-slate-700"><Sparkles className="w-4 h-4 text-emerald-500" /> {b}</li>)}
                </ul>
              </div>
              <Card className="p-6 sm:p-8 border-slate-200">
                <h3 className="font-display text-xl font-bold text-slate-900 mb-5">Apply to join</h3>
                <BecomeTutorForm />
              </Card>
            </div>
          </TabsContent>
        </Tabs>
      </Section>
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, testId }) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger data-testid={testId}><SelectValue placeholder={label} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="any">All {label}s</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}
